import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import TabSelect, { TabItem } from "@/components/tabSelect";
import InfoNote from "@/components/ui/InfoNote";
import LimitWarning from "@/components/ui/LimitWarning";
import Section from "@/components/ui/section";
import { MESSAGE } from "@/constants/message";
import useIsochroneCatchmentLimit from "@/hooks/spatialAnalysis/useIsochroneCatchmentLimit";
import { RootState } from "@/store";
import { setCatchmentSettingsTemp } from "@/store/slice/spatialSlice";
import { CatchmentType, ECatchmentType } from "@/types/spatial";

import CatchmentByRadius from "./catchmentByRadius";
import CatchmentByRoadAccess from "./catchmentByRoadAccess";

const CatchmentTypeSection = () => {
  const dispatch = useDispatch();
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const { limit, isRoadAccessOverLimit } = useIsochroneCatchmentLimit();

  const isRoadAccessSelected =
    catchmentSettingsTemp.catchmentType === ECatchmentType.BY_ROAD;

  const catchmentTypeList: TabItem[] = useMemo(
    () => [
      {
        label: "By Radius",
        value: ECatchmentType.BY_RADIUS
      },
      {
        label: "By Road Access",
        value: ECatchmentType.BY_ROAD,
        disabled: isRoadAccessOverLimit && !isRoadAccessSelected
      }
    ],
    [isRoadAccessOverLimit, isRoadAccessSelected]
  );

  return (
    <div className="flex flex-col px-4 gap-2">
      {isRoadAccessOverLimit &&
        (isRoadAccessSelected ? (
          <LimitWarning
            title="Caution"
            ariaLabel="Road access limit warning"
            message={MESSAGE.CATCHMENT_ROAD_ACCESS_OVER_LIMIT_CAUTION(limit)}
          />
        ) : (
          <InfoNote
            ariaLabel="Road access limit information"
            message={MESSAGE.CATCHMENT_ROAD_ACCESS_OVER_LIMIT_INFO(limit)}
          />
        ))}
      <Section label="Catchment Type">
        <TabSelect
          className="h-[32px]"
          buttonClassName="text-xs h-[24px]"
          wrapperListClassName="w-full"
          tabList={catchmentTypeList}
          selected={catchmentSettingsTemp.catchmentType}
          onSelect={(data) =>
            dispatch(
              setCatchmentSettingsTemp({ catchmentType: data as CatchmentType })
            )
          }
        />
      </Section>
      {catchmentSettingsTemp.catchmentType === ECatchmentType.BY_RADIUS && (
        <CatchmentByRadius />
      )}
      {isRoadAccessSelected && <CatchmentByRoadAccess />}
    </div>
  );
};

export default CatchmentTypeSection;
